// TelcoTrack - Root Application Component
// Main application shell with theme management and global layout

import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  Router,
  NavigationEnd,
  NavigationStart,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { Subject, interval } from 'rxjs';
import { takeUntil, switchMap, distinctUntilChanged, filter } from 'rxjs/operators';

// Services
import { ThemeService } from './core/services/theme.service';
import { AuthService } from './core/services/auth.service';
import { NotificationService } from './core/services/notification.service';
import { DiarySessionService } from './core/services/diary-session.service';
import type { Notification } from './core/models/notification.model';
import type { ConstructionDiary } from './core/models/construction-diary.model';
import { environment } from '../environments/environment';
import { PageTouchReplay } from './core/page-touch-replay';
import {
  ROLES,
  canAccessWorkReportMenu,
  canAccessPdChangesNotes,
  canAccessAuthorityPrepAgenda,
  canAccessAuthorityBremenaAgenda,
  canViewAuthorityEmailMailbox,
  canViewAuthorityIsdsMailbox,
  canAccessFakturace,
  canAccessImportMenu,
  canViewNavolavaniMenu,
  canOpenCompanyList,
  canViewOtherUsersActivityAndWorkReport,
  hasOrgAdminRole,
  canViewEquipmentSection,
  canViewMaterialsSection,
  hasPermission,
  isUnsetPermissionProfile,
  normalizeUserPermissionProfile,
  type AppPermissionSectionId,
  type UserPermissionProfile,
  EQUIPMENT_HANDOVER_NOTIFICATION_OBJECT_TYPE,
  NAVOLAVANI_CALLBACK_REMINDER_OBJECT_TYPE,
  WORK_ASSIGNMENT_NOTIFICATION_OBJECT_TYPE,
  isWarehouseTransferConfirmNotification,
  warehouseTransferConfirmationActionLabel,
  warehouseTransferConfirmKindFromPhase,
  WAREHOUSE_TRANSFER_NOTIFICATION_OBJECT_TYPE,
  WAREHOUSE_TRANSFER_REJECT_ACTION_CS,
} from '@core/shared-bridge';
import { WarehouseService } from './core/services/warehouse.service';
import { PageScrollPositionService } from './core/services/page-scroll-position.service';
import { webCanAccessConstructionDiary } from './core/utils/construction-diary-access.web';
import { restoreFocus, trapTabKey } from './shared/utils/focus-trap.util';
import { lockBodyScroll, unlockBodyScroll } from './shared/utils/body-scroll-lock.util';
import {
  TopbarSectionTabsService,
  type TopbarSectionTabRow,
} from './core/services/topbar-section-tabs.service';
import { TopbarPageTitleService } from './core/services/topbar-page-title.service';
import type { FilterMultiOption } from './shared/utils/filter-multi.util';
import { EQUIPMENT_HUB_UI_ENABLED } from './features/equipment/equipment-hub.flags';
import {
  SHELL_HOME_MODULES,
  SHELL_LANDING_PATH,
  SHELL_SETTINGS,
  isShellSettingsPath,
  shellBreadcrumbs,
  shellEntityTabLink,
  shellModuleFromPath,
  shellModuleIconPaths,
  shellModuleIsLeaf,
  shellModuleVisible,
  shellNestedBack,
  shellPathname,
  shellSectionFromPath,
  shellSettingsReturnTarget,
  shellSidebarCategories,
  shellSectionIconPaths,
  shellRowsForSection,
  shellSidebarRows,
  shellTabIsCurrent,
  shellTabIconPaths,
  shellTabsBesideModuleSections,
  shellVisibleSections,
  isShellHomePath,
  type ShellBack,
  type ShellCrumb,
  type ShellModuleDef,
  type ShellSectionDef,
} from './core/navigation/shell-nav';
import {
  SidebarHubNavService,
  type SidebarHubNavState,
} from './core/services/sidebar-hub-nav.service';
import { SidebarCollapseService } from './core/services/sidebar-collapse.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  // Component lifecycle
  private readonly destroy$ = new Subject<void>();
  
  // State
  isAuthenticated = false;
  isPrivacyRoute = false;
  currentTheme = 'light';
  isLoading = true;
  routeNavigationLoading = false;
  /** Lazy-chunk / guard failure — empty outlet would otherwise look like a blank page. */
  routeLoadError = '';
  settingsDropdownOpen = false;
  notificationsDropdownOpen = false;
  sidebarCollapsed = false;
  /** Sliding rail indicator on the desktop sidebar list (px from list top). */
  sidebarIndicatorY = 0;
  sidebarIndicatorH = 18;
  sidebarIndicatorReady = false;
  /** Off-canvas mobile navigation drawer (below `MOBILE_NAV_BREAKPOINT`) — see `toggleMobileMenu`. */
  mobileMenuOpen = false;
  isAdmin = false;
  isSuperAdmin = false;
  isStavba = false;
  /** Stavba, admin nebo superadmin — menu a modal stavebního deníku (viz `webCanAccessConstructionDiary`). */
  canViewConstructionDiary = false;
  canViewWorkReport = false;
  /** Menu Uživatelé — adresář a cizí profily (finance/admin; ne čistě terénní role). */
  canBrowseUsersDirectory = false;
  /** Menu Firmy — seznam firem. Bez profilu ho vidí každý, kdo už vidí adresář. */
  canViewCompaniesMenu = false;
  /** Menu Import — role import / superadmin. */
  canViewImport = false;
  /** Menu Fakturace — finance / admin / superadmin. */
  canViewFakturace = false;
  /** Menu Změny oproti PD — dokumentarista. */
  canViewPdChanges = false;
  /** Menu Příprava staveb — role priprava_staveb. */
  canViewPripravaStaveb = false;
  /** Menu Inženýring — role bremena (label Inženýring). */
  canViewInzenyring = false;
  /** Menu Email (`/urady/komunikace/email`). */
  canViewEmail = false;
  /** Menu Datovka (`/urady/komunikace/datovka`). */
  canViewDatovka = false;
  canViewNavolavani = false;
  /** Menu Zařízení — skladová API role (stejně jako hub Sklady). */
  canViewEquipment = false;
  /** Menu z profilu oprávnění. Bez profilu z /me zůstávají u role (níže). */
  canViewDashboard = true;
  canViewWorkQueue = true;
  canViewMaps = true;
  canViewPolygons = true;
  canViewObjects = true;
  canViewMaterials = true;
  canViewDatabase = false;
  canViewAppSettings = false;

  // Construction diary modal (globally handled — může se spustit z kteréhokoliv view po commitu)
  diaryModalOpen = false;
  activeDiaryEntry: ConstructionDiary | null = null;
  canConfirmDiary = false;

  // Notifications from API
  notifications: Notification[] = [];
  unreadCount = 0;
  // Notification status filter (icon, same row as heading)
  notificationFilterOptions: FilterMultiOption[] = [
    { value: 'all', label: 'Všechna oznámení' },
    { value: 'unread', label: 'Nepřečtená' },
  ];
  selectedNotificationFilters: string[] = ['unread'];
  transferActionNotificationId: string | null = null;
  notificationActionError: string | null = null;
  readonly transferRejectActionLabel = WAREHOUSE_TRANSFER_REJECT_ACTION_CS;
  topbarSectionTabRows: TopbarSectionTabRow[] = [];
  /** Page title registered by app-page-header — used in the breadcrumb, rendered in the body. */
  topbarPageTitle = '';
  /** Optional secondary line. Used as the entity crumb when the title repeats the module name. */
  topbarPageSubtitle = '';
  /** Contextual hub nav (App Nastavení) replacing main sidebar links. */
  sidebarHubNav: SidebarHubNavState = {
    active: false,
    hubTitle: '',
    backRouterLink: [SHELL_LANDING_PATH],
    backLabel: 'Přehled',
    items: [],
  };
  /** Home icon and post-login target: Dashboard přehled. */
  readonly shellLandingPath = SHELL_LANDING_PATH;
  /** Categories the user has opened. The current route's category is added on navigation. */
  private readonly expandedCategoryIds = new Set<string>();
  private routeCategoryId: string | null = null;
  /** Last page outside Nastavení. The gear and the settings Zpět link both open it. */
  private settingsReturnUrl = SHELL_LANDING_PATH;
  private readonly NOTIFICATION_POLL_INTERVAL_MS = 60_000;
  /** Below this width (px): desktop rail is hidden and replaced by the off-canvas mobile drawer. */
  private readonly MOBILE_NAV_BREAKPOINT = 1024;
  /** Restores focus to the hamburger button when the mobile drawer closes. */
  private elementFocusedBeforeMobileMenu: HTMLElement | null = null;
  private mobileMenuScrollLockActive = false;
  /** Parent of the drawer before it is moved to `document.body`. */
  private mobileNavHome: HTMLElement | null = null;

  @ViewChild('settingsDropdownContainer', { static: false }) settingsDropdownContainerRef?: ElementRef<HTMLElement>;
  @ViewChild('notificationsDropdownContainer', { static: false }) notificationsDropdownContainerRef?: ElementRef<HTMLElement>;
  @ViewChild('mobileNavDrawer', { static: false }) mobileNavDrawerRef?: ElementRef<HTMLElement>;
  @ViewChild('mobileNavRoot', { static: false }) mobileNavRootRef?: ElementRef<HTMLElement>;
  @ViewChild('sidebarNavList', { static: false }) sidebarNavListRef?: ElementRef<HTMLUListElement>;

  private sidebarIndicatorRaf = 0;
  private sidebarIndicatorTimer: ReturnType<typeof setTimeout> | null = null;
  /** Last non-empty child rows, kept while a section list eases shut. */
  private readonly retainedSectionRows = new Map<string, TopbarSectionTabRow[]>();
  private readonly closingSectionKeys = new Set<string>();
  private readonly sectionCloseTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly sectionCloseMs = 200;
  /** iPad tap replay. Capture phase, so Leaflet cannot hide popup touches. */
  private readonly pageTouch = new PageTouchReplay();
  private detachPageTouch: (() => void) | null = null;

  constructor(
    private readonly themeService: ThemeService,
    private readonly authService: AuthService,
    private readonly notificationService: NotificationService,
    private readonly warehouseService: WarehouseService,
    private readonly diarySession: DiarySessionService,
    private readonly router: Router,
    private readonly titleService: Title,
    private readonly topbarSectionTabs: TopbarSectionTabsService,
    private readonly topbarPageTitleService: TopbarPageTitleService,
    private readonly sidebarHubNavService: SidebarHubNavService,
    private readonly sidebarCollapse: SidebarCollapseService,
    /** Registers router listeners for list→detail scroll restore. */
    _pageScroll: PageScrollPositionService,
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle(environment.app.name);
    this.initializeApp();
    this.sidebarCollapse.collapsed$.pipe(takeUntil(this.destroy$)).subscribe((collapsed) => {
      this.sidebarCollapsed = collapsed;
      this.scheduleSidebarIndicatorUpdate();
    });
    this.subscribeToServices();
    this.setupRouterNavigation();
    this.syncExpandedCategory(this.router.url);
    this.setupNotificationPolling();
    this.setupDiarySession();
    this.setupTopbarSectionTabs();
    this.setupTopbarPageTitle();
    this.setupSidebarHubNav();
    this.detachPageTouch = this.pageTouch.attach(document);
  }

  ngOnDestroy(): void {
    this.detachPageTouch?.();
    this.detachPageTouch = null;
    this.destroy$.next();
    this.destroy$.complete();
    if (this.sidebarIndicatorRaf) {
      cancelAnimationFrame(this.sidebarIndicatorRaf);
      this.sidebarIndicatorRaf = 0;
    }
    if (this.sidebarIndicatorTimer) {
      clearTimeout(this.sidebarIndicatorTimer);
      this.sidebarIndicatorTimer = null;
    }
    for (const timer of this.sectionCloseTimers.values()) clearTimeout(timer);
    this.sectionCloseTimers.clear();
    if (this.mobileMenuOpen) {
      this.restoreMobileNavPortal();
      this.mobileMenuOpen = false;
    }
    if (this.mobileMenuScrollLockActive) {
      unlockBodyScroll();
      this.mobileMenuScrollLockActive = false;
    }
  }

  /**
   * Initialize application
   */
  private initializeApp(): void {
    // Do not delay first paint — a 1s overlay on top of the index splash looked like a blank page.
    this.isLoading = false;
  }

  /**
   * Subscribe to service observables
   */
  private subscribeToServices(): void {
    // distinctUntilChanged: validateAuthentication() re-emits true on every /me;
    // without it, each authGuard hit would refresh notifications (2 HTTP) again.
    this.authService.isAuthenticated$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((isAuth: boolean) => {
        this.isAuthenticated = isAuth;
        if (isAuth) {
          this.notificationService.refresh();
          this.initializeUserRole();
          this.scheduleSidebarIndicatorUpdate();
        } else {
          this.sidebarIndicatorReady = false;
        }
      });

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.authService.isLoggedIn()) {
          this.initializeUserRole();
          this.scheduleSidebarIndicatorUpdate();
        }
      });

    this.themeService.currentTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe((theme: string) => {
        this.currentTheme = theme;
      });

    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => (this.notifications = list));

    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => (this.unreadCount = count));
  }

  /**
   * Display name for the current user (top-bar account menu) – celé jméno nebo email.
   */
  get currentUserDisplayName(): string {
    const user = this.authService.getCurrentUser();
    if (!user) return '';
    const first = (user.firstName ?? '').trim();
    const last = (user.lastName ?? '').trim();
    const full = [first, last].filter(Boolean).join(' ').trim();
    if (full) return full;
    return (user.email ?? '').trim() || 'Uživatel';
  }

  get currentUserEmail(): string {
    return (this.authService.getCurrentUser()?.email ?? '').trim();
  }

  get currentUserAssetCardLink(): string[] | null {
    const email = this.currentUserEmail;
    return email ? ['/users', email, 'majetkova-karta'] : null;
  }

  /**
   * Initialize user role flags
   */
  private initializeUserRole(): void {
    const user = this.authService.getCurrentUser();
    this.isSuperAdmin = false;
    this.isAdmin = false;
    this.isStavba = false;
    this.canViewConstructionDiary = false;
    this.canViewWorkReport = false;
    this.canBrowseUsersDirectory = false;
    this.canViewCompaniesMenu = false;
    this.canViewImport = false;
    this.canViewFakturace = false;
    this.canViewPdChanges = false;
    this.canViewPripravaStaveb = false;
    this.canViewInzenyring = false;
    this.canViewEmail = false;
    this.canViewDatovka = false;
    this.canViewNavolavani = false;
    this.canViewEquipment = false;

    if (user?.roles) {
      const roles = new Set(user.roles.map((role: string) => role.toLowerCase()));
      const rolesArr = user.roles as string[];
      this.isSuperAdmin = roles.has(ROLES.SUPERADMIN);
      this.isAdmin = roles.has(ROLES.ADMIN) || this.isSuperAdmin;
      this.isStavba = roles.has(ROLES.STAVBA);
      this.canViewConstructionDiary = webCanAccessConstructionDiary(rolesArr);
      this.canViewWorkReport = canAccessWorkReportMenu(rolesArr);
      this.canBrowseUsersDirectory =
        canViewOtherUsersActivityAndWorkReport(rolesArr) || hasOrgAdminRole(rolesArr);
      this.canViewImport = canAccessImportMenu(rolesArr);
      this.canViewFakturace = canAccessFakturace(rolesArr);
      this.canViewPdChanges = canAccessPdChangesNotes(rolesArr);
      this.canViewPripravaStaveb = canAccessAuthorityPrepAgenda(rolesArr);
      this.canViewInzenyring = canAccessAuthorityBremenaAgenda(rolesArr);
      this.canViewEmail = canViewAuthorityEmailMailbox(rolesArr);
      this.canViewDatovka = canViewAuthorityIsdsMailbox(rolesArr);
      this.canViewNavolavani = canViewNavolavaniMenu(rolesArr);
    }
    const sessionRoles = (user?.roles ?? []) as string[];
    this.canViewEquipment =
      EQUIPMENT_HUB_UI_ENABLED && canViewEquipmentSection(sessionRoles, user?.permissionProfile);
    this.canViewDashboard = true;
    this.canViewWorkQueue = true;
    this.canViewMaps = true;
    this.canViewPolygons = true;
    this.canViewObjects = true;
    this.canViewMaterials = canViewMaterialsSection(sessionRoles, user?.permissionProfile);
    this.canViewDatabase = this.isSuperAdmin;
    this.canViewAppSettings = this.isAdmin;
    const rawProfile = user?.permissionProfile;
    let appliedProfile: UserPermissionProfile | null = null;
    if (rawProfile) {
      const profile = normalizeUserPermissionProfile(rawProfile);
      if (!isUnsetPermissionProfile(profile)) {
        appliedProfile = profile;
        this.applyNavFromProfile(profile);
      }
    }
    this.canViewCompaniesMenu = appliedProfile
      ? canOpenCompanyList(appliedProfile)
      : this.canBrowseUsersDirectory;
  }

  /** Záložky menu. Ostatní moduly dál kontrolují roli — tenhle průchod jen skrývá položky. */
  private applyNavFromProfile(profile: UserPermissionProfile): void {
    const view = (section: AppPermissionSectionId) => hasPermission(profile, section, 'view');
    this.canViewDashboard = view('dashboard');
    this.canViewFakturace = view('fakturace');
    this.canViewWorkQueue = view('fronta-prace');
    this.canViewWorkReport = view('work-report');
    this.canViewMaps = view('maps');
    this.canViewPolygons = view('polygons');
    this.canViewObjects = view('objects');
    this.canViewConstructionDiary = view('diary');
    this.canViewNavolavani = view('navolavani');
    this.canViewInzenyring = view('inzenyring');
    this.canViewPripravaStaveb = view('priprava');
    this.canViewEmail = view('email');
    this.canViewDatovka = view('datovka');
    this.canViewPdChanges = view('pd-changes');
    this.canBrowseUsersDirectory = view('users') || canOpenCompanyList(profile);
    this.canViewImport = view('import');
    this.canViewDatabase = view('database');
    this.canViewAppSettings = view('app-settings');
  }

  /**
   * Pipe pending-diary events from DiarySessionService into the template-driven modal.
   * Modal je renderován globálně v `app.component.html`, aby se uměl otevřít z jakékoliv stránky
   * (po commitu v mapě, popupu polygonu apod.).
   */
  private setupDiarySession(): void {
    this.diarySession.pending$
      .pipe(takeUntil(this.destroy$))
      .subscribe((pending) => {
        if (!pending) {
          this.diaryModalOpen = false;
          this.activeDiaryEntry = null;
          return;
        }
        this.activeDiaryEntry = pending.entry;
        this.canConfirmDiary = this.computeCanConfirmDiary();
        this.diaryModalOpen = true;
      });
  }

  /** Uzavření deníku zůstává u role. Skrytí záložky Deník řeší profil (`diary.view`). */
  private computeCanConfirmDiary(): boolean {
    const user = this.authService.getCurrentUser();
    return webCanAccessConstructionDiary(user?.roles as string[] | undefined);
  }

  onDiaryModalClose(): void {
    this.diarySession.dismiss();
  }

  onDiaryEntrySaved(updated: ConstructionDiary): void {
    this.activeDiaryEntry = updated;
  }

  onDiaryEntryConfirmed(updated: ConstructionDiary): void {
    this.activeDiaryEntry = updated;
  }

  /**
   * Poll notifications when authenticated
   */
  private setupNotificationPolling(): void {
    this.authService.isAuthenticated$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        switchMap(isAuth =>
          isAuth
            ? interval(this.NOTIFICATION_POLL_INTERVAL_MS).pipe(
                // Initial load already refreshed on auth true; poll only on ticks.
                filter(() => typeof document === 'undefined' || document.visibilityState === 'visible'),
              )
            : []
        )
      )
      .subscribe(() => this.notificationService.refresh());
  }

  /**
   * Setup router navigation to handle same-route clicks and standalone routes (privacy)
   */
  private setupRouterNavigation(): void {
    this.updatePrivacyRouteFlag(this.router.url);
    this.captureSettingsReturn(this.router.url);
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.captureSettingsReturn(this.router.url);
          this.routeNavigationLoading = true;
          this.routeLoadError = '';
          return;
        }
        if (
          event instanceof NavigationEnd
          || event instanceof NavigationCancel
          || event instanceof NavigationError
        ) {
          this.routeNavigationLoading = false;
        }
        if (event instanceof NavigationError) {
          const url = event.url || this.router.url || '';
          this.routeLoadError =
            url.startsWith('/materials')
              ? 'Stránku Sklady se nepodařilo načíst. Obnovte stránku, nebo se vraťte na přehled.'
              : url.startsWith('/equipment')
                ? 'Stránku Zařízení se nepodařilo načíst. Obnovte stránku, nebo se vraťte na přehled.'
                : 'Stránku se nepodařilo načíst. Obnovte stránku, nebo se vraťte na přehled.';
          return;
        }
        if (event instanceof NavigationEnd) {
          const currentUrl = event.urlAfterRedirects || event.url;
          this.updatePrivacyRouteFlag(currentUrl);
          this.syncExpandedCategory(currentUrl);
          // Off-canvas drawer must not stay open over the newly navigated page on mobile.
          if (this.mobileMenuOpen) {
            this.closeMobileMenu();
          }
          this.scheduleSidebarIndicatorUpdate();
        }
      });
  }

  /**
   * Update flag for standalone routes (privacy) – no topbar/sidebar
   */
  private updatePrivacyRouteFlag(url: string): void {
    this.isPrivacyRoute = url === '/privacy' || url.startsWith('/privacy/');
  }

  private setupTopbarSectionTabs(): void {
    this.topbarSectionTabs.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.topbarSectionTabRows = state.rows;
        this.scheduleSidebarIndicatorUpdate();
      });
  }

  private setupTopbarPageTitle(): void {
    this.topbarPageTitleService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.topbarPageTitle = state.title;
        this.topbarPageSubtitle = state.subtitle;
      });
  }

  get shellPath(): string {
    return shellPathname(this.router.url);
  }

  get shellIsHome(): boolean {
    return isShellHomePath(this.shellPath);
  }

  get shellModule(): ShellModuleDef | null {
    return shellModuleFromPath(this.shellPath);
  }

  /** Modules in the sidebar. Nastavení replaces that list while its route is open. */
  get shellCategories(): ShellModuleDef[] {
    const user = this.authService.getCurrentUser();
    return shellSidebarCategories(this.shellPath).filter((mod) => shellModuleVisible(mod.id, user));
  }

  /** Back appears only inside a nested entity and goes one level up. */
  get shellBack(): ShellBack | null {
    if (this.shellIsHome) return null;
    return shellNestedBack(this.shellPath);
  }

  /** True while the URL is `/app-nastaveni`. The sidebar lists only Nastavení. */
  get shellSettingsOpen(): boolean {
    return isShellSettingsPath(this.shellPath);
  }

  /** Where the gear and the settings Zpět link go. Celkový přehled when there is no previous page. */
  get settingsReturnLink(): string {
    return shellSettingsReturnTarget(this.settingsReturnUrl);
  }

  isSettingsModule(mod: ShellModuleDef): boolean {
    return mod.id === SHELL_SETTINGS.id;
  }

  isCategoryExpanded(id: string): boolean {
    if (this.shellSettingsOpen && id === SHELL_SETTINGS.id) return true;
    return this.expandedCategoryIds.has(id);
  }

  /** Mapa and any other module with no sections. The row is the link. */
  isLeafCategory(mod: ShellModuleDef): boolean {
    return shellModuleIsLeaf(mod);
  }

  /**
   * Children stay in the drawer. The collapsed desktop rail is category icons only,
   * except Nastavení, whose section icons stay on the rail.
   */
  showCategoryChildren(mod: ShellModuleDef): boolean {
    if (this.isLeafCategory(mod) || !this.isCategoryExpanded(mod.id)) return false;
    if (this.shellSettingsOpen && this.isSettingsModule(mod)) return true;
    return !this.sidebarCollapsed || this.mobileMenuOpen;
  }

  /** True while the browser click after a sidebar replay must not toggle again. */
  private shellClickSuppressed(): boolean {
    return this.pageTouch.suppressed();
  }

  toggleShellCategory(id: string): void {
    if (this.shellClickSuppressed()) return;
    const mod = id === SHELL_SETTINGS.id
      ? SHELL_SETTINGS
      : SHELL_HOME_MODULES.find((item) => item.id === id);
    if (mod && shellModuleIsLeaf(mod)) return;
    if (this.sidebarCollapsed && !this.mobileMenuOpen) {
      this.expandedCategoryIds.add(id);
      this.sidebarCollapse.setCollapsed(false);
      this.scheduleSidebarIndicatorUpdate();
      return;
    }
    if (this.shellSettingsOpen && id === SHELL_SETTINGS.id) return;
    if (this.expandedCategoryIds.has(id)) this.expandedCategoryIds.delete(id);
    else this.expandedCategoryIds.add(id);
    this.scheduleSidebarIndicatorUpdate();
  }

  moduleIconPaths(mod: ShellModuleDef): readonly string[] {
    return shellModuleIconPaths(mod);
  }

  categorySections(mod: ShellModuleDef): ShellSectionDef[] {
    return shellVisibleSections(mod, this.authService.getCurrentUser());
  }

  /** Deeper rows of this section. Empty for every category except the open route. */
  sectionChildRows(mod: ShellModuleDef, section: ShellSectionDef): TopbarSectionTabRow[] {
    const live = this.liveSectionChildRows(mod, section);
    const key = this.sectionDisclosureKey(mod, section);
    if (live.length) {
      this.retainedSectionRows.set(key, live);
      this.closingSectionKeys.delete(key);
      const pending = this.sectionCloseTimers.get(key);
      if (pending) {
        clearTimeout(pending);
        this.sectionCloseTimers.delete(key);
      }
      return live;
    }
    if (!this.retainedSectionRows.has(key)) return [];
    if (this.prefersReducedMotion()) {
      this.retainedSectionRows.delete(key);
      this.closingSectionKeys.delete(key);
      return [];
    }
    if (!this.closingSectionKeys.has(key)) {
      this.closingSectionKeys.add(key);
      const timer = setTimeout(() => {
        this.retainedSectionRows.delete(key);
        this.closingSectionKeys.delete(key);
        this.sectionCloseTimers.delete(key);
        this.scheduleSidebarIndicatorUpdate();
      }, this.sectionCloseMs);
      this.sectionCloseTimers.set(key, timer);
    }
    return this.retainedSectionRows.get(key) ?? [];
  }

  /** Open while this section's own page is showing children. Closing lists ease shut. */
  sectionPanelOpen(mod: ShellModuleDef, section: ShellSectionDef): boolean {
    return this.liveSectionChildRows(mod, section).length > 0 || this.sectionBack(mod, section) != null;
  }

  /**
   * Teal row follows the URL. A Zařízení child stays dark once the route is Sklady,
   * even if that list is still easing closed or its stored activeId is still Registr.
   */
  isShellTabCurrent(row: TopbarSectionTabRow, tabId: string): boolean {
    return shellTabIsCurrent(this.router.url, row, tabId);
  }

  /**
   * Category is the current page only while its children are hidden
   * (collapsed rail, or the user closed the category). An open category
   * leaves the teal on the section or tab the URL actually names.
   */
  isShellCategoryCurrent(mod: ShellModuleDef): boolean {
    return mod.id === this.shellModule?.id && !this.showCategoryChildren(mod);
  }

  /** Section row is current only when it is open and no child tab is the page. */
  isShellSectionCurrent(mod: ShellModuleDef, section: ShellSectionDef): boolean {
    if (!this.showCategoryChildren(mod)) return false;
    if (mod.id !== this.shellModule?.id || section.id !== this.shellActiveSectionId) return false;
    const live = this.liveSectionChildRows(mod, section);
    return !live.some((row) => row.tabs.some((tab) => this.isShellTabCurrent(row, tab.id)));
  }

  /**
   * Nested tab follows the URL. A closed section keeps its rows only while they
   * ease shut, and those rows must not stay highlighted.
   */
  isShellChildCurrent(
    mod: ShellModuleDef,
    section: ShellSectionDef,
    row: TopbarSectionTabRow,
    tabId: string,
  ): boolean {
    if (!this.showCategoryChildren(mod) || !this.sectionPanelOpen(mod, section)) return false;
    return this.isShellTabCurrent(row, tabId);
  }

  private liveSectionChildRows(mod: ShellModuleDef, section: ShellSectionDef): TopbarSectionTabRow[] {
    if (mod.id !== this.shellModule?.id) return [];
    return shellRowsForSection(section.id, this.topbarSectionTabRows, this.shellPath);
  }

  private sectionDisclosureKey(mod: ShellModuleDef, section: ShellSectionDef): string {
    return `${mod.id}:${section.id}`;
  }

  private prefersReducedMotion(): boolean {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * One step up, under the section it belongs to. Skipped when that section
   * link is already the same destination (Polygony, Firmy).
   */
  sectionBack(mod: ShellModuleDef, section: ShellSectionDef): ShellBack | null {
    if (mod.id !== this.shellModule?.id || section.id !== this.shellActiveSectionId) return null;
    const back = this.shellBack;
    if (!back || back.link === section.route) return null;
    if (back.label.trim().toLocaleLowerCase('cs') === section.label.trim().toLocaleLowerCase('cs')) return null;
    return back;
  }

  /** Current route's category starts expanded. A manual collapse holds until the category changes. */
  private syncExpandedCategory(url: string): void {
    const mod = shellModuleFromPath(url);
    const id = mod?.id ?? null;
    if (id && id !== this.routeCategoryId) {
      if (mod && !shellModuleIsLeaf(mod)) this.expandedCategoryIds.add(id);
      this.routeCategoryId = id;
    } else if (!id) {
      this.routeCategoryId = null;
    }
  }

  /** Remember the page the user was on before opening Nastavení. */
  private captureSettingsReturn(url: string): void {
    if (isShellSettingsPath(url)) return;
    this.settingsReturnUrl = shellSettingsReturnTarget(url);
  }

  /** Gear opens Nastavení, and leaves it again for the page the user came from. */
  onSettingsGear(event: Event): void {
    if (this.isModifiedClick(event)) return;
    event.preventDefault();
    this.closeAccountMenu();
    if (this.shellSettingsOpen) {
      void this.router.navigateByUrl(this.settingsReturnLink);
      return;
    }
    void this.router.navigateByUrl('/app-nastaveni');
  }

  /** Zpět above the Nastavení heading. Same destination as clicking the gear again. */
  leaveAppSettings(event: Event): void {
    if (this.isModifiedClick(event)) return;
    event.preventDefault();
    this.closeAccountMenu();
    void this.router.navigateByUrl(this.settingsReturnLink);
  }

  private isModifiedClick(event: Event): boolean {
    if (!(event instanceof MouseEvent)) return false;
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  get shellActiveSectionId(): string {
    return shellSectionFromPath(this.shellPath)?.id ?? '';
  }

  sectionIconPaths(section: ShellSectionDef): readonly string[] {
    return shellSectionIconPaths(section);
  }

  tabIconPaths(tab: { id: string; label: string }): readonly string[] {
    return shellTabIconPaths(tab);
  }

  trackShellModule(_index: number, mod: ShellModuleDef): string {
    return mod.id;
  }

  trackShellSection(_index: number, section: ShellSectionDef): string {
    return section.id;
  }

  trackTopbarSectionTabRow(_index: number, row: TopbarSectionTabRow): symbol {
    return row.ownerId;
  }

  trackShellTab(_index: number, tab: { id: string }): string {
    return tab.id;
  }

  get shellTabRows(): TopbarSectionTabRow[] {
    const rows = shellSidebarRows(this.topbarSectionTabRows, this.shellPath);
    if (this.shellBack) return rows;
    return shellTabsBesideModuleSections(rows);
  }

  get shellActiveTabLabel(): string {
    const row = this.shellTabRows[0];
    if (!row) return '';
    return row.tabs.find((tab) => tab.id === row.activeId)?.label ?? '';
  }

  get shellCrumbs(): ShellCrumb[] {
    return shellBreadcrumbs({
      url: this.shellPath,
      pageTitle: this.shellIsHome ? '' : this.topbarPageTitle,
      pageSubtitle: this.shellIsHome ? '' : this.topbarPageSubtitle,
      activeTabLabel: this.shellActiveTabLabel,
    });
  }

  onShellSectionTab(row: TopbarSectionTabRow, tabId: string): void {
    if (this.shellClickSuppressed()) return;
    const tab = row.tabs.find((item) => item.id === tabId);
    if (!tab || tab.disabled) return;
    // Call the row the sidebar is showing. `selectTab` used to return before this
    // when the registration was missing or the id already matched, so the click died.
    const delivered = this.topbarSectionTabs.selectTab(row.ownerId, tabId);
    if (!delivered) {
      row.onActiveIdChange(tabId);
      const link = shellEntityTabLink(this.router.url, row.idPrefix, tabId);
      if (link) {
        void this.router.navigate(link.commands, {
          queryParams: link.queryParams,
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    }
    this.closeAccountMenu();
  }

  private setupSidebarHubNav(): void {
    this.sidebarHubNavService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.sidebarHubNav = state;
        this.scheduleSidebarIndicatorUpdate();
      });
  }

  /**
   * Handle theme toggle (top-bar theme switch).
   */
  onThemeToggle(): void {
    if (this.shellClickSuppressed()) return;
    this.themeService.toggleTheme();
  }

  /** Czech label for the top-bar theme switch (tooltip). */
  get themeToggleLabel(): string {
    return this.currentTheme === 'light' ? 'Tmavý režim' : 'Světlý režim';
  }

  /**
   * Closes the account menu. The mobile drawer stays until this click finishes:
   * unmounting it, or moving focus back to the hamburger, in the same turn drops
   * the RouterLink click on iOS (the tap never starts navigation).
   */
  closeAccountMenu(): void {
    this.settingsDropdownOpen = false;
    setTimeout(() => {
      if (!this.destroy$.closed) this.closeMobileMenu();
    });
  }

  /**
   * Toggle sidebar expanded/collapsed (icons-only when collapsed). Desktop-only rail behavior —
   * persisted so returning users keep their preference. The mobile drawer (`mobileMenuOpen`)
   * always shows full labels and is never affected by this flag.
   */
  toggleSidebar(): void {
    if (this.shellClickSuppressed()) return;
    this.sidebarCollapse.toggle();
  }

  /** If the viewport grows past the mobile breakpoint while the drawer is open, the desktop rail
   *  takes over — the off-canvas drawer would otherwise stay open behind it. */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.mobileMenuOpen && !this.isMobileViewport()) {
      this.closeMobileMenu();
    }
    this.scheduleSidebarIndicatorUpdate();
  }

  /** Move the shared sidebar rail indicator to the active `.nav-link`. */
  private scheduleSidebarIndicatorUpdate(): void {
    if (this.sidebarIndicatorRaf) cancelAnimationFrame(this.sidebarIndicatorRaf);
    this.sidebarIndicatorRaf = requestAnimationFrame(() => {
      this.sidebarIndicatorRaf = 0;
      // Second frame: wait for the route's active class and *ngIf nav items to settle.
      this.sidebarIndicatorRaf = requestAnimationFrame(() => {
        this.sidebarIndicatorRaf = 0;
        this.updateSidebarIndicator();
      });
    });
    if (this.sidebarIndicatorTimer) clearTimeout(this.sidebarIndicatorTimer);
    // The disclosure eases for 200ms; measure again once the row has finished moving.
    this.sidebarIndicatorTimer = setTimeout(() => {
      this.sidebarIndicatorTimer = null;
      this.updateSidebarIndicator();
    }, this.sectionCloseMs);
  }

  private updateSidebarIndicator(): void {
    const list = this.sidebarNavListRef?.nativeElement;
    if (!list || !this.isAuthenticated || this.isPrivacyRoute) {
      this.sidebarIndicatorReady = false;
      return;
    }
    const active = this.visibleSidebarActiveLink(list);
    if (!active) {
      this.sidebarIndicatorReady = false;
      return;
    }
    const top = this.indicatorTopWithin(active, list);
    if (top == null) {
      this.sidebarIndicatorReady = false;
      return;
    }
    const stripH = 18; // 1.125rem — matches previous ::before height
    const y = top + (active.getBoundingClientRect().height - stripH) / 2;
    this.sidebarIndicatorY = Math.max(0, y);
    this.sidebarIndicatorH = stripH;
    this.sidebarIndicatorReady = true;
  }

  /** The open row. A child inside a shut disclosure must not keep the rail. */
  private visibleSidebarActiveLink(list: HTMLElement): HTMLElement | null {
    const links = list.querySelectorAll<HTMLElement>('.nav-link.active');
    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      if (link.closest('.sidebar-disclosure:not(.sidebar-disclosure--open)')) continue;
      if (link.getClientRects().length === 0) continue;
      return link;
    }
    return null;
  }

  /**
   * Content Y of `el` inside the scroll list. `offsetTop` stops at a positioned
   * ancestor, so the rail was a row or more below the current label.
   * Viewport delta plus the list's scrollTop is the same origin as `top` on the strip.
   */
  private indicatorTopWithin(el: HTMLElement, list: HTMLElement): number | null {
    const elRect = el.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    if (elRect.height === 0 || elRect.width === 0) return null;
    return elRect.top - listRect.top + list.scrollTop;
  }

  private isMobileViewport(): boolean {
    return globalThis.window !== undefined && globalThis.window.innerWidth < this.MOBILE_NAV_BREAKPOINT;
  }

  /**
   * Open/close the off-canvas mobile navigation drawer (hamburger button, below ~1024px).
   * Traps focus, locks background scroll, and restores focus to the trigger on close —
   * same pattern as `app-modal` (see `shared/utils/focus-trap.util.ts`).
   */
  toggleMobileMenu(): void {
    if (this.shellClickSuppressed()) return;
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  private openMobileMenu(): void {
    this.elementFocusedBeforeMobileMenu = document.activeElement as HTMLElement | null;
    this.mobileMenuOpen = true;
    this.settingsDropdownOpen = false;
    this.notificationsDropdownOpen = false;
    if (!this.mobileMenuScrollLockActive) {
      lockBodyScroll();
      this.mobileMenuScrollLockActive = true;
    }
    // Wait a tick so the *ngIf drawer exists, then leave overflow:hidden / transformed
    // ancestors. A fixed drawer inside them is painted on screen on iOS but taps hit
    // the backdrop, so the menu never navigates.
    setTimeout(() => {
      if (!this.mobileMenuOpen) return;
      this.portalMobileNav();
      const drawer = this.mobileNavDrawerRef?.nativeElement;
      if (drawer && typeof drawer.focus === 'function') {
        drawer.focus({ preventScroll: true });
      }
    });
  }

  /** Fixed overlay must live on `document.body` (same reason as `app-modal`). */
  private portalMobileNav(): void {
    const root = this.mobileNavRootRef?.nativeElement;
    if (!root || typeof document === 'undefined') return;
    if (root.parentElement !== document.body) {
      this.mobileNavHome = root.parentElement;
      document.body.appendChild(root);
    }
    // Show only after the move. A slide that starts inside the shell keeps
    // the iOS hit box on the backdrop.
    root.classList.add('mobile-nav-root--ready');
  }

  private restoreMobileNavPortal(): void {
    const root = this.mobileNavRootRef?.nativeElement;
    const home = this.mobileNavHome;
    if (root) root.classList.remove('mobile-nav-root--ready');
    if (!root || !home || root.parentElement === home) return;
    home.appendChild(root);
  }

  closeMobileMenu(): void {
    if (!this.mobileMenuOpen) return;
    this.restoreMobileNavPortal();
    this.mobileMenuOpen = false;
    if (this.mobileMenuScrollLockActive) {
      unlockBodyScroll();
      this.mobileMenuScrollLockActive = false;
    }
    restoreFocus(this.elementFocusedBeforeMobileMenu);
    this.elementFocusedBeforeMobileMenu = null;
  }

  /** Escape closes the drawer; Tab is trapped inside it while open. */
  onMobileMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.closeMobileMenu();
      return;
    }
    if (event.key === 'Tab' && this.mobileNavDrawerRef) {
      trapTabKey(event, this.mobileNavDrawerRef.nativeElement);
    }
  }

  /**
   * Close settings and notifications dropdowns when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.settingsDropdownOpen && this.settingsDropdownContainerRef) {
      const clickedInside = this.settingsDropdownContainerRef.nativeElement.contains(event.target as Node);
      if (!clickedInside) {
        this.settingsDropdownOpen = false;
      }
    }
    if (this.notificationsDropdownOpen && this.notificationsDropdownContainerRef) {
      const target = event.target;
      const clickedInside = this.notificationsDropdownContainerRef.nativeElement.contains(target as Node);
      // Filter panel is portaled to document.body — clicks there must not close the dropdown.
      const clickedFilterPanel = target instanceof Element && !!target.closest('.tt-filter-multi-select__panel');
      if (!clickedInside && !clickedFilterPanel) {
        this.notificationsDropdownOpen = false;
      }
    }
  }

  /** Escape closes settings / notifications panels (mobile drawer has its own handler). */
  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    if (this.mobileMenuOpen) {
      return;
    }
    if (this.settingsDropdownOpen || this.notificationsDropdownOpen) {
      this.settingsDropdownOpen = false;
      this.notificationsDropdownOpen = false;
    }
  }

  /**
   * Toggle the top-bar account menu.
   */
  toggleSettingsDropdown(): void {
    if (this.shellClickSuppressed()) return;
    this.settingsDropdownOpen = !this.settingsDropdownOpen;
    this.notificationsDropdownOpen = false;
  }

  /**
   * Toggle notifications dropdown and load list when opening
   */
  toggleNotificationsDropdown(): void {
    if (this.shellClickSuppressed()) return;
    this.notificationsDropdownOpen = !this.notificationsDropdownOpen;
    this.settingsDropdownOpen = false;
    if (this.notificationsDropdownOpen) {
      this.loadNotifications();
    }
  }

  /**
   * Load notifications list from API
   */
  loadNotifications(): void {
    const filter = this.selectedNotificationFilters[0] === 'unread' ? 'unread' : 'all';
    this.notificationService.getNotifications(filter).subscribe();
  }

  /**
   * Status filter is exclusive (all vs unread), matching the previous select.
   */
  onNotificationFilterChange(selected: string[]): void {
    const prev = this.selectedNotificationFilters[0];
    let next: 'all' | 'unread';
    if (selected.length === 0) {
      next = 'all';
    } else if (selected.length === 1 && (selected[0] === 'all' || selected[0] === 'unread')) {
      next = selected[0];
    } else {
      const added = selected.find((value) => value !== prev);
      next = added === 'unread' ? 'unread' : 'all';
    }
    this.selectedNotificationFilters = [next];
    this.loadNotifications();
  }

  /**
   * Mark single notification as read
   */
  markNotificationAsRead(n: Notification): void {
    if (n.readAt) return;
    this.notificationService.markAsRead(n.id).subscribe();
  }

  /**
   * Open the related map object from a notification while keeping the existing read-state behavior.
   */
  onNotificationClick(n: Notification): void {
    this.markNotificationAsRead(n);
    this.notificationsDropdownOpen = false;
    if (this.isWarehouseTransferNotification(n)) {
      void this.router.navigate(['/materials/warehouse/transfers']);
      return;
    }
    const objectType = String(n.objectType ?? '').trim().toUpperCase();
    if (objectType === WORK_ASSIGNMENT_NOTIFICATION_OBJECT_TYPE) {
      const workObjectId = String(n.objectId ?? '').trim();
      if (workObjectId && !workObjectId.startsWith('work:')) {
        void this.router.navigate(['/maps'], { queryParams: { objectId: workObjectId } });
      } else {
        void this.router.navigate(['/fronta-prace']);
      }
      return;
    }
    if (objectType === NAVOLAVANI_CALLBACK_REMINDER_OBJECT_TYPE) {
      const navObjectId = String(n.objectId ?? '').trim();
      if (navObjectId) {
        void this.router.navigate(['/maps'], { queryParams: { objectId: navObjectId } });
      } else {
        void this.router.navigate(['/navolavani']);
      }
      return;
    }
    if (objectType === EQUIPMENT_HANDOVER_NOTIFICATION_OBJECT_TYPE) {
      if (this.canViewEquipment) {
        void this.router.navigate(['/equipment/transfers'], {
          queryParams: { potvrzeni: '1' },
        });
      } else {
        const link = this.currentUserAssetCardLink;
        if (link) {
          void this.router.navigate(link, { queryParams: { tab: 'pending' } });
        } else {
          void this.router.navigate([SHELL_LANDING_PATH]);
        }
      }
      return;
    }
    if (!n.objectId) return;
    void this.router.navigate(['/maps'], { queryParams: { objectId: n.objectId } });
  }

  isTransferConfirmNotification(n: Notification): boolean {
    return isWarehouseTransferConfirmNotification(n.phaseCode);
  }

  isWarehouseTransferNotification(n: Notification): boolean {
    return String(n.objectType ?? '').trim().toUpperCase() === WAREHOUSE_TRANSFER_NOTIFICATION_OBJECT_TYPE;
  }

  transferConfirmActionLabel(n: Notification): string {
    return warehouseTransferConfirmationActionLabel(warehouseTransferConfirmKindFromPhase(n.phaseCode));
  }

  confirmTransferFromNotification(n: Notification, event?: Event): void {
    event?.stopPropagation();
    this.actOnTransferNotification(n, true);
  }

  rejectTransferFromNotification(n: Notification, event?: Event): void {
    event?.stopPropagation();
    this.actOnTransferNotification(n, false);
  }

  private actOnTransferNotification(n: Notification, confirm: boolean): void {
    const id = String(n.objectId ?? '').trim();
    if (!id || this.transferActionNotificationId) return;
    this.transferActionNotificationId = n.id;
    this.notificationActionError = null;
    const request$ = confirm
      ? this.warehouseService.confirmTransfer(id)
      : this.warehouseService.rejectTransfer(id);
    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.transferActionNotificationId = null;
        this.markNotificationAsRead(n);
        this.notificationService.showSuccess(
          confirm ? 'Převod schválen a proveden.' : 'Převod odmítnut.',
        );
        this.loadNotifications();
        this.notificationService.getUnreadCount().subscribe();
      },
      error: (err: { error?: { message?: string }; message?: string }) => {
        this.transferActionNotificationId = null;
        this.notificationActionError =
          err?.error?.message ?? err?.message ?? (confirm ? 'Potvrzení převodu selhalo' : 'Odmítnutí převodu selhalo');
      },
    });
  }

  /**
   * Mark all notifications as read and immediately refresh list (u filtru Nepřečtená zmizí)
   */
  markAllNotificationsAsRead(): void {
    this.notificationService.markAllAsRead().subscribe(() => {
      this.loadNotifications();
    });
  }

  /**
   * Format notification timestamp for display
   */
  formatNotificationTime(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Právě teď';
    if (diffMins < 60) return `Před ${diffMins} min`;
    if (diffHours < 24) return `Před ${diffHours} h`;
    if (diffDays === 1) return 'Včera';
    if (diffDays < 7) return `Před ${diffDays} dny`;
    return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
  }

  /**
   * Handle logout
   */
  onLogout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.notificationService.showSuccess('Úspěšně odhlášen');
        this.settingsDropdownOpen = false;
        this.router.navigate(['/login']);
      },
      error: (error) => {
        console.error('Logout error:', error);
        // Even if logout fails, navigate to login
        this.settingsDropdownOpen = false;
        this.router.navigate(['/login']);
      }
    });
  }
}
